<template>
  <Page>
    <template #title>
      Loans
    </template>

    <Table
      date-key="completedAt"
      :items="loans"
      :total-item-count="loanCount"
      :count="{ singular: 'loan', plural: 'loans' }"
      :state="state"
      :items-per-page="pageSize"
      item-type="Loan"
      :headers="tableHeaders"
      hideHeader
      table-id="loans-table"
      @next="goToNextPage"
      @prev="goToPreviousPage"
    >
      <template #tableHeader>
        <div>
          <div class="flex flex-wrap items-end gap-2">
            <div class="w-full max-w-xs">
              <TbInput
                v-model="search"
                label="Search"
                placeholder="Customer / OrderID / Product"
              />
            </div>

            <TbDateRangePicker
              :dates="dates"
              @selected-date-range="setDateRange"
            />
            <div class="flex justify-between flex-grow">
              <OrderExportCsvButtonGroup @download="exportCsv" />
              <Filter
                class="self-center"
                :showFilters="showFilter"
                :toggleFilter="toggleFilters"
                :cancelFilters="cancelFilters"
                :applyFilters="applyFilters"
                :filters="{
                  status: {
                    value: 'status',
                    label: 'By Status',
                    options: statusFilters,
                  },
                }"
              />
            </div>
          </div>
        </div>
        <div
          v-if="selectedStatus.length > 0 || selectedType.length > 0"
          class="flex flex-wrap gap-2 mt-4"
        >
          <button
            v-for="status in selectedStatus"
            :key="status.value"
            class="flex items-center border btn btn-sm border-primary"
            @click="removeStatusFilters(status)"
          >
            <TbXMarkIcon class="w-4 h-4 mr-2" />
            <span>{{ status.label }}</span>
          </button>
          <button
            v-for="type in selectedType"
            :key="type.value"
            class="flex items-center border btn btn-sm border-primary"
            @click="removeTypeFilters(type)"
          >
            <TbXMarkIcon class="w-4 h-4 mr-2" />
            <span>{{ type.label }}</span>
          </button>
        </div>
      </template>

      <template #action="{ item, toggleDropdown }">
        <div class="flex flex-col h-full gap-1">
          <RefundForm
            :pending-operation="item.existingFundedOperation"
            :is-refundable="item.isRefundable"
            :order="{ ...item, product: item.productName, studentName: item.customerName, origin: 'lnpl' }"
            :refund-enabled="projectConfig.configs.refundEnabled"
            @refund="loadLoans()"
          />

          <CancelOrderForm
            :pending-operation="item.existingFundedOperation"
            :is-cancellable="item.isCancellable"
            :order="{ ...item, product: item.productName, studentName: item.customerName, origin: 'lnpl' }"
            @cancel-order="loadLoans()"
          />

          <button
            class="btn btn-block btn-sm btn-ghost text-left flex gap-2 items-center"
            @click="toggleDropdown(); copyLoanId(item)"
          >
            <TbCopyIcon class="w-4 h-4" />
            Copy Loan ID
          </button>
        </div>
      </template>

      <template #row(id)="{ item }">
        <div class="truncate w-36">
          <routerLink
            :to="{ name: Pages.dashboardLoan, params: { id: item.id } }"
            class="text-secondary underline"
          >
            <span>{{ item.id }}</span>
          </routerLink>
        </div>
      </template>

      <template #row(checkoutName)="{ value }">
        <div class="max-w-[150px] break-words whitespace-normal">
          {{ value }}
        </div>
      </template>

      <template #row(customerName)="{ item }">
        <routerLink :to="{ name: Pages.dashboardStudent, params: { id: item.customerId } }">
          <div class="flex flex-col">
            <span>{{ item.customerName }}</span>
            <span class="text-xs text-gray-500">
              {{ item.customerEmail }}
            </span>
          </div>
        </routerLink>
      </template>

      <template #row(term)="{ item }">
        <div class="flex flex-col">
          <span>{{ item.term > 1 ? `${item.term} Months` : `1 Month` }}</span>
        </div>
      </template>

      <template #row(amountInCents)="{ item }">
        <div class="flex flex-col">
          <span class="currency">{{ `${item.currency}${currencyInCents(item.amountInCents)}` }}</span>
        </div>
      </template>

      <template #row(status)="{ value }">
        <div>
          <div>
            <span
              class="inline-block w-2 h-2 mr-1 rounded-full"
              :class="orderStatusToColorMap[snakeToPascal(value)]"
            />
            <span>{{ snakeToPascal(value) }}</span>
          </div>
        </div>
      </template>
    </Table>
  </Page>
  <QuickStartGuideLink />

  <TbOverlay
    :show="csvState === PageState.loading"
    persistent
  >
    <div class="flex items-center justify-center w-full h-full">
      <TbLoading />
    </div>
  </TbOverlay>

  <TbNotification
    :show="copyNotification"
    title="Success 🎉"
    :message="copyNotificationText"
    card-class="text-white bg-success"
    @close="copyNotification = false"
  />
</template>

<script setup lang="ts">
import Filter, { FilterOption } from '@/components/filter.vue';
import QuickStartGuideLink from '@/components/quick_start_guide_link.vue';
import Table from '@/components/table.vue';
import {
  TbCopyIcon,
  TbDateRangePicker,
  TbInput,
  TbLoading,
  TbNotification,
  TbOverlay, TbXMarkIcon,
} from '@/components/tasty_bistro';
import { currencyInCents } from '@/filters/currency';
import { formatDate } from '@/filters/date';
import { orderStatusToColorMap } from '@/helpers/category_color_mapper';
import { downloadFile } from '@/helpers/download_file';
import { snakeToPascal } from '@/helpers/strings';
import { ProjectState } from '@/pages/app/project/api/get_project_state';
import { Pages } from '@/router';
import { PageState } from '@/types';
import { useClipboard } from '@vueuse/core';
import { subMonths } from 'date-fns';
import { debounce } from 'lodash';
import {
  Ref,
  inject,
  onActivated,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import Page from '../components/page.vue';
import { Loan, getLoans } from './api/get_loans';
import { getOrdersDownload } from './api/get_orders_download';
import OrderExportCsvButtonGroup from './components/order_export_csv_button_group.vue';
import CancelOrderForm from './forms/cancel_order.vue';
import RefundForm from './forms/refund_order.vue';

const loans = ref<Loan[]>();
const search = ref('');
const csvState = ref(PageState.loaded);
const state: Ref<PageState> = ref(PageState.loading);
const showFilter = ref(false);
const copyNotification = ref(false);
const copyNotificationText = ref('');
const showDropdown = ref(false);

type Dates = [Date, Date]
const dates = ref<Dates>([subMonths(new Date(), 6), new Date()]);
const pageSize = 20;
const currentPage = ref(0);
const loanCount = ref<number>(0);
const statusFilters = ref<FilterOption[]>([]);
const selectedStatus = ref<FilterOption[]>([]);
const selectedType = ref<FilterOption[]>([]);

const typeFilters = [
  { label: 'Funding', checked: false, value: 'lnpl' },
  { label: 'In-House', checked: false, value: 'ppm' },
];

const tableHeaders = [
  {
    title: 'Date',
    key: 'completedAt',
    formatter: (val: string) => formatDate(val, 'MMM dd, yyyy'),
  },
  {
    title: 'Id',
    key: 'id',
  },
  {
    title: 'Customer',
    key: 'customerName',
  },
  {
    title: 'Checkout',
    key: 'checkoutName',
  },
  {
    title: 'Term',
    key: 'term',
  },
  {
    title: 'Total',
    key: 'amountInCents',
  },
  {
    title: 'Status',
    key: 'status',
  },
];

const projectState = inject<Ref<ProjectState>>('project') as Ref<ProjectState>;
const projectConfig = projectState.value;
const route = useRoute();
const projectId = route.params.projectId as string;

const loadLoans = async () => {
  try {
    state.value = PageState.loading;
    const data = await getLoans({
      projectId,
      filter: {
        startDate: dates.value[0],
        endDate: dates.value[1],
        pageSize,
        offset: currentPage.value * pageSize,
        search: search.value,
        status: selectedStatus.value.map((s) => s.value),
        origin: selectedType.value.map((t) => t.value),
      },
    });

    loans.value = data.loans;
    loanCount.value = Number(data.totalCount);
    statusFilters.value = data.statusFilters.map(
      (sf) => ({
        ...sf,
        checked: selectedStatus.value
          .map((ss) => ss.value).includes(sf.value),
      }),
    );
    state.value = PageState.loaded;
  } catch (error) {
    state.value = PageState.error;
  }
};

const exportCsv = async (format: 'order_tax_report' | 'orders') => {
  try {
    csvState.value = PageState.loading;
    const file = await getOrdersDownload({
      format,
      projectId,
      filter: {
        startDate: dates.value[0],
        endDate: dates.value[1],
        search: search.value,
        status: selectedStatus.value.map((s) => s.value),
        origin: selectedType.value.map((t) => t.value),
      },
    });
    downloadFile({ file, fileName: `elective_${format}.csv` });
    csvState.value = PageState.loaded;
  } catch (error) {
    csvState.value = PageState.error;
  }
  if (showDropdown.value) {
    showDropdown.value = false;
  }
};

const copyLoanId = (order: Loan) => {
  copyNotification.value = false;

  setTimeout(() => {
    const clipboard = useClipboard({ source: order.id });
    clipboard.copy();
    copyNotificationText.value = 'Order ID has been successfully copied to your Clipboard';
    copyNotification.value = true;
  }, 200);
};

onActivated(async () => {
  await loadLoans();
});

const setDateRange = async (newDates: Dates) => {
  currentPage.value = 0;
  dates.value = newDates;
  await loadLoans();
};

watch(search, debounce(async () => {
  currentPage.value = 0;
  await loadLoans();
}, 500));

const goToNextPage = async (page: number) => {
  currentPage.value = page;
  await loadLoans();
};

const goToPreviousPage = async (page: number) => {
  currentPage.value = page;
  await loadLoans();
};

const toggleFilters = () => {
  showFilter.value = !showFilter.value;
};

const applyFilters = async () => {
  showFilter.value = false;
  selectedStatus.value = statusFilters.value?.filter((s) => s.checked) || [];
  selectedType.value = typeFilters.filter((t) => t.checked);
  await loadLoans();
};

const removeStatusFilters = async (status: FilterOption) => {
  selectedStatus.value = selectedStatus.value.filter((ss) => ss.value !== status.value);
  statusFilters.value
    .forEach((sf) => {
      if (sf.value === status.value) {
        sf.checked = false;
      }
    });

  await loadLoans();
};

const removeTypeFilters = async (type: FilterOption) => {
  selectedType.value = selectedType.value.filter((st) => st.value !== type.value);
  typeFilters
    .forEach((sf) => {
      if (sf.value === type.value) {
        sf.checked = false;
      }
    });
  await loadLoans();
};

const cancelFilters = () => {
  showFilter.value = false;
};
</script>

<style lang="scss">
.table {
  @apply bg-secondary-100 w-full
}

.student-more-btn {
  @apply py-0 px-2 ml-2
}

#loans-table tbody tr {
  vertical-align: top;
  text-align: start;
}

.custom-disabled {
  @apply cursor-not-allowed opacity-[0.35];
}
</style>
